import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { accuracyPercent, normalizeIeltsTeacherReport } from '../lib/practiceAttempts';
import { formatIeltsBand, IELTS_DIMENSION_LABELS, IELTS_DIMENSIONS } from '../lib/ieltsSpeaking';

const firstDefinedReport = (report, key) => (
  report?.first_attempt?.[key]
  ?? report?.first_attempt_aggregates?.[key]
  ?? report?.first_attempt_summary?.[key]
  ?? null
);

export default function AdminPracticeReportSummary({ report, t }) {
  if (!report) return null;
  const firstAccuracy = accuracyPercent(firstDefinedReport(report, 'accuracy'));
  const completion = accuracyPercent(report.completion_rate);
  const isIelts = report.practice_type === 'ielts_speaking' || report.assignment?.practice_type === 'ielts_speaking';
  const ielts = isIelts ? normalizeIeltsTeacherReport(report) : null;
  const predictedBand = ielts?.first_attempt?.predicted_overall;
  const targetBand = ielts?.assignment?.config?.target_band ?? ielts?.summary?.target_band;
  const targetMetCount = Number(ielts?.summary?.target_met_count || 0);
  const targetNotMetCount = Number(ielts?.summary?.target_not_met_count || 0);
  return (
    <section className="mt-6 border-y border-gray-200 py-5" aria-label={t('Assignment report')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('Assignment report')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('Detailed metrics are shown inside this assignment report.')}</p>
        </div>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700">
          <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
          {completion == null ? t('Not available') : `${completion}% ${t('completed')}`}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 border border-gray-200 sm:grid-cols-3">
        <div className="border-b border-gray-200 p-3 sm:border-b-0 sm:border-r">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Completion rate')}</div>
          <div className="mt-1 text-lg font-bold text-gray-900">{completion == null ? t('Not available') : `${completion}%`}</div>
        </div>
        <div className="border-b border-gray-200 p-3 sm:border-b-0 sm:border-r">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('First-attempt accuracy')}</div>
          <div className="mt-1 text-lg font-bold text-gray-900">{firstAccuracy == null ? t('Not available') : `${firstAccuracy}%`}</div>
        </div>
        <div className="p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('First-attempt sample size')}</div>
          <div className="mt-1 text-lg font-bold text-gray-900" data-i18n-skip>{report.first_attempt_sample_size}</div>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-500">{t('First-attempt aggregates only · sample size {count}', { count: report.first_attempt_sample_size })}</p>
      {ielts && (
        <div className="mt-5 border-t border-gray-200 pt-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="font-bold text-gray-900">{t('IELTS first-attempt snapshot')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('Teacher reports use the first attempt as the aggregate baseline.')}</p>
            </div>
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Predicted band')}</div>
              <div className="text-2xl font-bold tabular-nums text-indigo-800" data-i18n-skip>{predictedBand == null ? t('Not available') : formatIeltsBand(predictedBand)}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 border border-gray-200 sm:grid-cols-4">
            {IELTS_DIMENSIONS.map((key) => {
              const value = ielts.first_attempt?.dimensions?.[key];
              return (
                <div key={key} className="border-b border-gray-200 p-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(IELTS_DIMENSION_LABELS[key])}</div>
                  <div className="mt-1 text-lg font-bold tabular-nums text-gray-900" data-i18n-skip>{value == null ? t('Not available') : formatIeltsBand(value)}</div>
                </div>
              );
            })}
          </div>
          {targetBand != null && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 rounded border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
              <span>{t('Target band')}: <strong data-i18n-skip>{formatIeltsBand(targetBand)}</strong></span>
              <span>{t('{count} students reached the target', { count: targetMetCount })}</span>
              <span>{t('{count} students completed below the target', { count: targetNotMetCount })}</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
